/**
 * Bert client-side filter
 * --------------------------------------------------------------------
 * Replaces Shopify's slow AJAX filter (1-2s server roundtrip) with
 * instant client-side show/hide based on data-bert-* attributes on
 * each .grid__item. Intercepts filter clicks before Dawn's facets.js
 * submits the form, applies filter locally, updates URL via pushState.
 *
 * Fallback: if anything errors, the original Dawn form submission is
 * NOT prevented (server-side filter still works).
 *
 * Scope: collection pages only. Detects context via #product-grid.
 */
(function () {
  'use strict';

  // Bert default sort: ensure /collections/all loads with sort_by=price-descending
  // so the displayed dropdown label ("Price, high to low") matches what Shopify
  // actually serves. Without this, a bare /collections/all URL returns items in
  // Shopify's default collection order (Featured/Manual), and the dropdown label
  // is misleading. Redirect runs once on landing, replaces the URL so users
  // don't see /collections/all in their history.
  (function ensureDefaultSort() {
    if (window.location.pathname !== '/collections/all') return;
    var params = new URLSearchParams(window.location.search);
    if (params.has('sort_by')) return;
    params.set('sort_by', 'price-descending');
    window.location.replace(window.location.pathname + '?' + params.toString());
  })();

  var GRID_SELECTOR = '#product-grid';
  var FILTER_FORM_SELECTOR = 'facet-filters-form, #FacetFiltersForm';
  var SIDEBAR_SELECTOR = '#FacetsWrapperDesktop, .facets-vertical';

  // Map Shopify filter URL-param prefix → data-* attribute on grid item.
  // Example: ?filter.p.m.custom.character=Foo → matches data-bert-character
  var FILTER_PARAM_MAP = {
    'filter.p.m.custom.character': 'bertCharacter',
    'filter.p.m.custom.card_type': 'bertCardType',
    'filter.p.m.custom.grade': 'bertGrade',
    'filter.p.m.custom.set': 'bertSet',
    'filter.p.m.custom.grading_company': 'bertGradingCompany',
    'filter.p.m.custom.language': 'bertLanguage',
    'filter.p.tag': 'bertTags',
  };

  // Taxonomy filters (Shopify-defined) use GIDs as URL values, e.g.
  // ?filter.v.t.shopify.grading=gid://shopify/TaxonomyValue/15104. We can't
  // match GIDs directly against product data, so on init we read each
  // taxonomy checkbox's label text and build a GID → label map. Then at
  // filter-match time we lookup the label and check it against bertTags.
  // Map of filter-param → which data attribute to match against.
  // Map Shopify Taxonomy filter URL-param prefix → data-* attribute.
  // The "Grading" taxonomy attribute is mirrored into data-bert-grading
  // (lowercased label like "psa" or "bgs") so client-side matching uses
  // the SAME data source as Shopify's server-side filter - not tags.
  var TAXONOMY_FILTER_MAP = {
    'filter.v.t.shopify.grading': 'bertGrading',
    // Game (Shopify category metafield 'Theme'): One Piece vs Riftbound etc.
    'filter.v.t.shopify.theme': 'bertTheme',
  };
  var taxonomyGidToLabel = {};        // lowercased - for tag matching
  var taxonomyGidToDisplay = {};      // original case - for pill display

  function buildTaxonomyMap() {
    Object.keys(TAXONOMY_FILTER_MAP).forEach(function (paramName) {
      document.querySelectorAll('input[name="' + paramName + '"]').forEach(function (input) {
        var gid = input.value;
        if (!gid || taxonomyGidToLabel[gid]) return;
        // Look in either .facets__item (desktop) or .mobile-facets__item (mobile)
        var item = input.closest('.facets__item, .mobile-facets__item');
        if (!item) return;
        var labelEl = item.querySelector('.facet-checkbox__text-label, .mobile-facets__label');
        if (labelEl) {
          var raw = labelEl.textContent.trim();
          // Strip trailing " (N)" count suffix if present (Dawn renders these)
          var cleaned = raw.replace(/\s*\(\d+\)\s*$/, '').trim();
          taxonomyGidToLabel[gid] = cleaned.toLowerCase();
          taxonomyGidToDisplay[gid] = cleaned;
        }
      });
    });
  }

  // ----- helpers -----

  function getGrid() {
    var el = document.querySelector(GRID_SELECTOR);
    if (!el) return null;
    // On COLLECTION pages #product-grid IS the <ul class="product-grid">, but on
    // SEARCH it is a <div class="template-search__results"> that WRAPS the <ul>.
    // That distinction matters: resortGroupDuplicates() re-appends every card to
    // this element, so returning the wrapper would physically move the <li>s out
    // of the <ul>. Dawn's column rules (.grid--4-col-desktop .grid__item) only
    // match items INSIDE the grid, so the cards lost their widths and collapsed
    // into a single column. Always operate on the <ul> when one exists.
    if (el.tagName !== 'UL') {
      var ul = el.querySelector('ul.product-grid') || el.querySelector('ul.grid');
      if (ul) return ul;
    }
    return el;
  }

  function getActiveFilters() {
    // Parse current URL params into { paramName: [values...] } map.
    var params = new URLSearchParams(window.location.search);
    var active = {};
    params.forEach(function (value, key) {
      if (!key.startsWith('filter.')) return;
      if (!active[key]) active[key] = [];
      active[key].push(value);
    });
    return active;
  }

  function itemMatches(item, activeFilters) {
    // For each active filter, check the item has a matching value.
    // AND across param types, OR within values of same param type.
    var ds = item.dataset;
    for (var key in activeFilters) {
      var values = activeFilters[key];
      if (key === 'filter.v.price.gte') {
        var price = parseFloat(ds.bertPrice);
        var gte = parseFloat(values[0]);
        if (isNaN(price) || price < gte) return false;
        continue;
      }
      if (key === 'filter.v.price.lte') {
        var p2 = parseFloat(ds.bertPrice);
        var lte = parseFloat(values[0]);
        if (isNaN(p2) || p2 > lte) return false;
        continue;
      }
      // Standard custom-metafield filter
      var dataKey = FILTER_PARAM_MAP[key];
      // Taxonomy filter - resolve GID to label first, then match against
      // either dedicated data attribute (bertGrading) OR fall back to tags
      // for products that have tags but no taxonomy value set yet.
      var isTaxonomy = !dataKey && TAXONOMY_FILTER_MAP[key];
      if (isTaxonomy) dataKey = TAXONOMY_FILTER_MAP[key];
      if (!dataKey) continue; // truly unknown filter - let it pass
      var itemValue = (ds[dataKey] || '').toLowerCase();
      // Fallback: if taxonomy data attribute is empty, try product tags
      if (isTaxonomy && !itemValue) {
        itemValue = (ds.bertTags || '').toLowerCase();
      }
      if (!itemValue) return false;
      var checkValues = values;
      if (isTaxonomy) {
        // values are GIDs; map to label text via lookup
        checkValues = values.map(function (gid) { return taxonomyGidToLabel[gid] || ''; }).filter(Boolean);
        if (!checkValues.length) return false; // unknown GID → no match
      }
      // Treat as comma-separated for tags AND multi-value taxonomy attributes
      // (bertGrading). Single-value metafields collapse to a one-item array.
      if (dataKey === 'bertTags' || dataKey === 'bertGrading' || dataKey === 'bertTheme') {
        var itemValues = itemValue.split(',').map(function (t) { return t.trim(); });
        var anyMatch = checkValues.some(function (v) { return itemValues.indexOf(v.toLowerCase()) !== -1; });
        if (!anyMatch) return false;
      } else {
        var anyValMatch = checkValues.some(function (v) { return v.toLowerCase() === itemValue; });
        if (!anyValMatch) return false;
      }
    }
    return true;
  }

  // Client-side resort to honor the current sort_by URL param. Generalised
  // from a previous "always price DESC, title ASC" implementation so that
  // when the user picks a different sort option in the dropdown the grid
  // reorders instantly without a page reload.
  // Supported sort_by values (match Shopify's standard collection sorts):
  //   price-descending, price-ascending, title-ascending, title-descending,
  //   created-descending, created-ascending, best-selling, manual
  // For unknown / best-selling / manual we fall back to the natural DOM
  // order Shopify gave us, with the title tiebreaker for equal prices to
  // still cluster duplicate listings.
  function getTitleText(el) {
    var node = el.querySelector('.card__heading, .card-information__text, .full-unstyled-link');
    return ((node && node.textContent) || '').trim().toLowerCase();
  }
  function resortGroupDuplicates(grid, items) {
    if (!items || items.length < 2) return;
    var sortBy = new URLSearchParams(window.location.search).get('sort_by') || 'price-descending';
    var arr = Array.prototype.slice.call(items);
    arr.forEach(function (el, idx) { el._bertOrigIdx = idx; });
    arr.sort(function (a, b) {
      var pa = parseFloat(a.dataset.bertPrice);
      var pb = parseFloat(b.dataset.bertPrice);
      if (isNaN(pa)) pa = -Infinity;
      if (isNaN(pb)) pb = -Infinity;
      var ta = getTitleText(a);
      var tb = getTitleText(b);
      var primary = 0;
      if (sortBy === 'price-ascending') {
        primary = pa - pb;
      } else if (sortBy === 'title-ascending') {
        primary = ta < tb ? -1 : ta > tb ? 1 : 0;
      } else if (sortBy === 'title-descending') {
        primary = ta < tb ? 1 : ta > tb ? -1 : 0;
      } else if (sortBy === 'price-descending') {
        primary = pb - pa;
      } else {
        // best-selling, created-*, manual: keep DOM order as primary
        primary = a._bertOrigIdx - b._bertOrigIdx;
      }
      if (primary !== 0) return primary;
      // Secondary: title ASC so duplicate listings cluster
      if (ta < tb) return -1;
      if (ta > tb) return 1;
      // Final stability tiebreaker
      return a._bertOrigIdx - b._bertOrigIdx;
    });
    // Re-append in new order. Items that haven't changed position skip
    // the DOM op via appendChild's "same parent, same position" no-op.
    arr.forEach(function (el) { grid.appendChild(el); });
  }

  function applyFilter() {
    var grid = getGrid();
    if (!grid) return;
    var items = grid.querySelectorAll('.grid__item');
    var active = getActiveFilters();
    var shown = 0;
    items.forEach(function (item) {
      if (itemMatches(item, active)) {
        item.classList.remove('bert-filter-hidden');
        shown++;
      } else {
        item.classList.add('bert-filter-hidden');
      }
    });
    // Secondary sort: within whatever primary sort the server gave us
    // (sort_by URL param drives this), group duplicates by re-sorting
    // items with EQUAL price next to each other by title. Shopify's
    // sort_by only accepts a single field, so identical cards listed
    // multiple times wouldn't cluster on a pure price sort. Stable
    // sort: items that compare equal preserve insertion order.
    try { resortGroupDuplicates(grid, items); } catch (e) {}
    // Update product count if Dawn's counter exists
    var counter = document.getElementById('ProductCount');
    var counterDesktop = document.getElementById('ProductCountDesktop');
    var countText = shown + ' of ' + items.length + ' products';
    if (counter) counter.textContent = countText;
    if (counterDesktop) counterDesktop.textContent = countText;
    // Mobile filter drawer count. It is a CLASS (not one of the ids above), so
    // it was never updated and kept showing the unfiltered total (e.g. "94
    // products" while 3 cards were shown). The element also contains a
    // " . Reset" separator + link, so replace ONLY its leading text node
    // instead of clobbering innerHTML and destroying the Reset link.
    document.querySelectorAll('.mobile-facets__count').forEach(function (el) {
      var first = el.firstChild;
      if (first && first.nodeType === 3) first.textContent = countText + ' ';
    });
    // Dynamic faceting: update counts on each filter option + hide 0-count.
    // Wrapped in try/catch so any bug in count rendering doesn't break the
    // primary filter behavior (product show/hide above).
    try {
      updateFilterOptionCounts(items, active);
    } catch (e) {
      console.warn('[bert-filter] dynamic facet count failed', e);
    }
    return shown;
  }

  function updateFilterOptionCounts(allItems, activeFilters) {
    // For each filter option (checkbox or price preset), compute would-be
    // product count and visually gray out 0-count options (instead of hiding).
    // Targets BOTH desktop (.facets__item / .facet-checkbox__text) and mobile
    // (.mobile-facets__item / .mobile-facets__label) checkbox structures.
    // Rule: count = how many products this option has WITHIN the other-type
    // filters (price etc.), IGNORING any same-type selection already made.
    // Reads as "if you add Manga, those 4 Manga products would be added".
    // Multi-select is allowed for all metafield filters - never gray out
    // an option just because another value of the same type is already
    // checked.
    var checkboxes = document.querySelectorAll(
      '.facets__item input[type="checkbox"], .mobile-facets__item input[type="checkbox"]'
    );
    checkboxes.forEach(function (input) {
      var paramName = input.name;
      var paramValue = input.value;
      if (!paramName) return;
      var li = input.closest('.facets__item, .mobile-facets__item');
      if (!li) return;
      var isThisChecked = input.checked;
      var shouldGray = false;
      var count = 0;
      // Build hypothetical: keep other-type filters, set same-type to JUST
      // this option (ignore existing same-type selection). Gives the
      // "this option alone within current other-type filters" count.
      var hypothetical = {};
      for (var k in activeFilters) {
        if (k !== paramName) hypothetical[k] = activeFilters[k];
      }
      hypothetical[paramName] = [paramValue];
      allItems.forEach(function (item) {
        if (itemMatches(item, hypothetical)) count++;
      });
      if (count === 0 && !isThisChecked) shouldGray = true;
      // Update count text (desktop)
      var textWrap = li.querySelector('.facet-checkbox__text');
      if (textWrap) {
        var labelEl = textWrap.querySelector('.facet-checkbox__text-label');
        var labelText = labelEl ? labelEl.textContent.trim() : '';
        textWrap.innerHTML =
          '<span class="facet-checkbox__text-label">' + escapeHtml(labelText) + '</span> (' + count + ')';
      }
      // Update count text (mobile)
      var mobileLabel = li.querySelector('.mobile-facets__label');
      if (mobileLabel) {
        // Mobile label has rich content - only replace the trailing "(N)" or "(N products)" text
        var html = mobileLabel.innerHTML;
        // Replace last occurrence of "(N)" anywhere
        var newHtml = html.replace(/\((\d+)\)(\s*)<\/span>(\s*<span class="visually-hidden">)/, '(' + count + ')$2</span>$3');
        if (newHtml !== html) mobileLabel.innerHTML = newHtml;
      }
      // Apply / remove gray-out. Also include Dawn's server-side disabled
      // state: when Shopify renders a facet with count=0 it puts a
      // `disabled` class on the .facet-checkbox label and `disabled` attr
      // on the input. Treat those as 0-count too so .bert-filter-option-disabled
      // catches every variant (no need for :has() fallback in older browsers).
      var serverDisabled =
        input.hasAttribute('disabled') ||
        !!li.querySelector('.facet-checkbox.disabled, .mobile-facets__label.disabled');
      if (shouldGray || (serverDisabled && !isThisChecked)) {
        li.classList.add('bert-filter-option-disabled');
      } else {
        li.classList.remove('bert-filter-option-disabled');
      }
    });
    // Price presets - same gray-out logic
    updatePricePresetCounts(allItems, activeFilters);
  }

  function updatePricePresetCounts(allItems, activeFilters) {
    var presets = document.querySelectorAll('.bert-price-preset');
    var currentGte = activeFilters['filter.v.price.gte'] ? activeFilters['filter.v.price.gte'][0] : null;
    var currentLte = activeFilters['filter.v.price.lte'] ? activeFilters['filter.v.price.lte'][0] : null;
    presets.forEach(function (link) {
      if (link.classList.contains('bert-price-preset--clear')) return;
      var href = link.getAttribute('href') || '';
      var hrefQs = href.split('?')[1] || '';
      var hrefParams = new URLSearchParams(hrefQs);
      var presetGte = hrefParams.get('filter.v.price.gte');
      var presetLte = hrefParams.get('filter.v.price.lte');
      // Build hypothetical = other filter types + this price range
      var hypothetical = {};
      for (var k in activeFilters) {
        if (!k.startsWith('filter.v.price')) hypothetical[k] = activeFilters[k];
      }
      if (presetGte) hypothetical['filter.v.price.gte'] = [presetGte];
      if (presetLte) hypothetical['filter.v.price.lte'] = [presetLte];
      var count = 0;
      allItems.forEach(function (item) {
        if (itemMatches(item, hypothetical)) count++;
      });
      var isActive = link.classList.contains('is-active');
      if (count === 0 && !isActive) {
        link.classList.add('bert-filter-option-disabled');
      } else {
        link.classList.remove('bert-filter-option-disabled');
      }
    });
  }

  // ----- Intercept form input clicks -----

  function buildUrlFromForm(form) {
    // Read all checked inputs + <select> values from the facets form, build
    // query string. Reading <select> is critical: the sort dropdown is a
    // hidden <select name="sort_by"> inside the facet form. Without this,
    // every checkbox/slider change would silently drop sort_by from the URL,
    // and ensureDefaultSort would then redirect back to price-descending -
    // exactly the "sort dropdown reverts on change" flicker bug.
    var params = new URLSearchParams();
    var inputs = form.querySelectorAll('input');
    inputs.forEach(function (input) {
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (input.checked && input.name && input.value) {
          params.append(input.name, input.value);
        }
      } else if (input.type === 'hidden' && input.name && input.value) {
        params.append(input.name, input.value);
      }
    });
    var selects = form.querySelectorAll('select');
    selects.forEach(function (sel) {
      if (sel.name && sel.value) {
        params.append(sel.name, sel.value);
      }
    });
    // Fallback: if no <select sort_by> in form, preserve sort_by from current URL
    var current = new URLSearchParams(window.location.search);
    var sortBy = current.get('sort_by');
    if (sortBy && !params.has('sort_by')) {
      params.append('sort_by', sortBy);
    }
    // Bert: read the price slider's current min/max EVERY time (not just
    // when URL is missing them). The slider widget is the source of truth
    // for the current price range - reading it here ensures the price
    // filter survives every form rebuild, including races between
    // checkbox clicks and the slider's debounced apply.
    params.delete('filter.v.price.gte');
    params.delete('filter.v.price.lte');
    var slider = form.querySelector('[data-bert-price-slider]') || document.querySelector('[data-bert-price-slider]');
    if (slider) {
      var sliderMin = parseFloat(slider.getAttribute('data-slider-min')) || 0;
      var sliderMax = parseFloat(slider.getAttribute('data-slider-max')) || 100000;
      var minHandle = slider.querySelector('[data-bert-slider-handle="min"]');
      var maxHandle = slider.querySelector('[data-bert-slider-handle="max"]');
      if (minHandle && maxHandle) {
        var sMinVal = parseFloat(minHandle.value);
        var sMaxVal = parseFloat(maxHandle.value);
        if (!isNaN(sMinVal) && sMinVal > sliderMin) params.append('filter.v.price.gte', sMinVal);
        if (!isNaN(sMaxVal) && sMaxVal < sliderMax) params.append('filter.v.price.lte', sMaxVal);
      }
    }
    return window.location.pathname + '?' + params.toString();
  }

  function onFormChange(e) {
    // CRITICAL: bail out when the price pill is being clicked. The text input
    // fires a `change` event on blur which bubbles to the form. Without this
    // guard, the change event runs onFormChange -> buildUrlFromForm (which
    // reads the slider handles and RE-APPENDS price filter to URL) ->
    // renderActivePills() replaces the price pill in DOM. That replacement
    // happens BETWEEN mousedown and mouseup, so the browser may not fire a
    // click event at all (click requires mousedown+mouseup on the same
    // target). Result: first pill click feels dead. Set in pill mousedown,
    // cleared after click handler runs.
    if (window.__bertSuppressFormChange) return;
    var form = e.target.closest('form');
    if (!form || !form.matches(FILTER_FORM_SELECTOR + ', ' + FILTER_FORM_SELECTOR + ' *') && !form.closest(FILTER_FORM_SELECTOR)) {
      var ff = e.target.closest('facet-filters-form');
      if (!ff) return;
      form = ff.querySelector('form') || form;
    }
    try {
      commit(buildUrlFromForm(form));
    } catch (err) {
      console.warn('[bert-filter] client-side failed, falling back to server', err);
    }
  }

  // Single URL-commit path used by every code that mutates filter state.
  // Replaces the five duplicated sequences of pushState + sync + apply +
  // render previously scattered across onFormChange, applySlider, the pill
  // click handler, interceptPricePresets, and interceptResetLink.
  function commit(newUrl) {
    window.history.pushState({ bertFilter: true }, '', newUrl);
    syncFormStateFromUrl();
    applyFilter();
    renderActivePills();
  }

  // ----- Active filter pills (client-side rendering) -----

  // Map filter-param key → display label prefix
  var PILL_LABEL_MAP = {
    'filter.p.m.custom.character': 'Character',
    'filter.p.m.custom.card_type': 'Card Type',
    'filter.p.m.custom.grade': 'Grade',
    'filter.p.m.custom.set': 'Set',
    'filter.p.m.custom.grading_company': 'Grading Company',
    'filter.v.t.shopify.grading': 'Grading Company', // taxonomy variant
    'filter.v.t.shopify.theme': 'Game',              // taxonomy (category metafield 'Theme')
    'filter.p.m.custom.language': 'Language',
    'filter.p.tag': null, // tags show as just the value, no prefix
  };

  function formatPriceLabel(gte, lte) {
    function fmt(n) { return '$' + Number(n).toLocaleString('en-US'); }
    // Suffix " AUD" so customers viewing in another currency (via Shopify
    // Markets) know the filter is bounded by your base AUD prices.
    var suffix = ' AUD';
    if (gte && lte) return fmt(gte) + ' - ' + fmt(lte) + suffix;
    if (gte && !lte) return fmt(gte) + '+' + suffix;
    if (!gte && lte) return 'Under ' + fmt(lte) + suffix;
    return '';
  }

  function buildPillHtml(label, removeUrl, removeKey, removeValue) {
    // Store the filter key/value on the pill as data attributes. The
    // click handler will use them to compute the remove URL FRESH from
    // the current window.location.search - that way rapid pill clicks
    // never reinstate filters via stale hrefs. href is kept as a
    // fallback for accessibility (keyboard nav, "open in new tab").
    // Special marker: removeKey="__price__" means remove both
    // filter.v.price.gte AND filter.v.price.lte.
    return (
      '<facet-remove>' +
      '<a href="' + removeUrl + '" class="active-facets__button active-facets__button--light"' +
      ' data-bert-remove-key="' + escapeHtml(removeKey || '') + '"' +
      ' data-bert-remove-value="' + escapeHtml(removeValue || '') + '">' +
      '<span class="active-facets__button-inner button button--tertiary">' +
      escapeHtml(label) +
      '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close-small" viewBox="0 0 12 13">' +
      '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"/></svg></span>' +
      '<span class="visually-hidden">Remove filter</span>' +
      '</span></a></facet-remove>'
    );
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function urlWithoutParam(key, value) {
    var params = new URLSearchParams(window.location.search);
    // Remove only the specific key=value pair
    var newParams = new URLSearchParams();
    params.forEach(function (v, k) {
      if (k === key && v === value) return; // skip this one
      newParams.append(k, v);
    });
    // Bert: ensure sort_by lands in the URL even when it wasn't there
    // before. Sidebar checkbox unticks go through buildUrlFromForm which
    // reads the hidden <select name="sort_by"> and includes its value;
    // pill X clicks used to skip that, dropping the sort context. Mirror
    // the behavior so both paths produce URLs with sort_by.
    if (!newParams.has('sort_by')) {
      var sortSelect = document.querySelector('select[name="sort_by"]');
      if (sortSelect && sortSelect.value) {
        newParams.append('sort_by', sortSelect.value);
      }
    }
    var qs = newParams.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function urlWithoutPrice() {
    var params = new URLSearchParams(window.location.search);
    var newParams = new URLSearchParams();
    params.forEach(function (v, k) {
      if (k === 'filter.v.price.gte' || k === 'filter.v.price.lte') return;
      newParams.append(k, v);
    });
    // Bert: see urlWithoutParam - mirror the same sort_by-add behavior so
    // removing the price pill also lands on a URL with sort_by.
    if (!newParams.has('sort_by')) {
      var sortSelect = document.querySelector('select[name="sort_by"]');
      if (sortSelect && sortSelect.value) {
        newParams.append('sort_by', sortSelect.value);
      }
    }
    var qs = newParams.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function renderActivePills() {
    // Render pills in BOTH desktop AND mobile containers so both stay in sync
    // after client-side filter changes. (Previously only desktop got updated
    // → mobile pills stuck on initial server render.)
    var containers = document.querySelectorAll(
      '.active-facets-desktop, .active-facets-mobile'
    );
    if (!containers.length) return;

    // Build pill HTML once
    var params = new URLSearchParams(window.location.search);
    var pillsHtml = [];

    var priceGte = params.get('filter.v.price.gte');
    var priceLte = params.get('filter.v.price.lte');
    if (priceGte || priceLte) {
      var priceLabel = 'Price: ' + formatPriceLabel(priceGte, priceLte);
      var priceRemoveUrl = urlWithoutPrice();
      pillsHtml.push(buildPillHtml(priceLabel, priceRemoveUrl, '__price__', ''));
    }

    params.forEach(function (value, key) {
      if (!key.startsWith('filter.')) return;
      if (key.startsWith('filter.v.price')) return;
      var prefix = PILL_LABEL_MAP[key];
      if (prefix === undefined) return;
      // For taxonomy GIDs, use the original-case label (e.g. "PSA" not "Psa")
      var displayValue = value;
      if (TAXONOMY_FILTER_MAP[key] && taxonomyGidToDisplay[value]) {
        displayValue = taxonomyGidToDisplay[value];
      }
      var label = prefix ? prefix + ': ' + displayValue : displayValue;
      var removeUrl = urlWithoutParam(key, value);
      pillsHtml.push(buildPillHtml(label, removeUrl, key, value));
    });

    var joined = pillsHtml.join('');

    containers.forEach(function (container) {
      // Remove existing facet-remove pills (direct children only, so we don't
      // touch nested structures inside bert-filter-row or wrapper elements).
      // Also keep the Remove all wrapper (hidden via CSS) so we don't disturb
      // Dawn's HTML structure.
      var existing = container.querySelectorAll(
        ':scope > facet-remove:not(.active-facets__button-wrapper)'
      );
      existing.forEach(function (p) { p.remove(); });
      if (joined) {
        // Insert before the Remove all wrapper if present, else at end
        var wrapper = container.querySelector(':scope > facet-remove.active-facets__button-wrapper');
        if (wrapper) {
          wrapper.insertAdjacentHTML('beforebegin', joined);
        } else {
          container.insertAdjacentHTML('beforeend', joined);
        }
      }
    });
  }

  // Delegate clicks on dynamically-added pill remove links. CAPTURE PHASE
  // (third arg `true`) is critical: Dawn's <facet-remove> binds
  // `this.closeFilter.bind(this)` to the inner <a> click in its constructor.
  // .bind() snapshots the function reference at bind time, so prototype-
  // patching closeFilter LATER doesn't affect already-bound listeners on
  // existing pills - they still call the original handler, which fires
  // form.onActiveFilterClick and triggers Dawn's server-side facet fetch.
  // That was the "first click on pill doesn't register" bug: Dawn's bound
  // handler fired first, did a server fetch, and the resulting DOM swap
  // dropped our update mid-flight. Re-registering at capture phase means
  // OUR handler runs BEFORE Dawn's element-bound handler. stopPropagation
  // then keeps Dawn's bound listener from ever seeing the event.
  // Also: compute the remove URL FRESH from window.location.search at click
  // time using the pill's stored key/value. Rapid clicks would otherwise
  // use stale hrefs from when the pill was originally rendered, causing
  // already-removed filters to be reinstated.
  // Mousedown on the price pill - set a flag so the slider text input's
  // blur handler (which fires BEFORE click in event order) doesn't re-apply
  // the price filter milliseconds after we remove it. Without this, the
  // first click on the price pill appeared to do nothing: blur fired,
  // applySlider re-added price.gte/lte from the still-populated text input,
  // pill was recreated, click then removed price - and any subsequent
  // event that called applySlider re-added it from the stale text input.
  // Mousedown on ANY pill - set guard flags BEFORE the focus shifts. Clicking
  // a pill while a slider text input has focus triggers (in order): blur on
  // the text input -> change on the text input bubbles to form -> form's
  // change listener runs onFormChange -> buildUrlFromForm reads the slider
  // handles and re-appends the price filter -> renderActivePills replaces
  // the pill in DOM. Because the pill DOM node was just swapped out between
  // mousedown and mouseup, the browser may decline to fire a click event
  // (click needs same target for mousedown and mouseup). Result: first
  // pill-click feels dead. Setting these flags in capture-phase mousedown
  // prevents the chain by short-circuiting applySlider AND onFormChange.
  document.addEventListener('mousedown', function (e) {
    var pill = e.target.closest('.active-facets-desktop > facet-remove > a, .active-facets-mobile > facet-remove > a');
    if (!pill) return;
    // Suppress BOTH paths for ANY pill, not just the price pill. When a price
    // filter is active and a slider TEXT INPUT currently has focus, clicking
    // ANY pill blurs that input -> its blur listener calls applySlider()
    // DIRECTLY (it does not go through the form change event, so
    // __bertSuppressFormChange alone is not enough). applySlider then commits
    // and renderActivePills() rebuilds every pill node in the DOM between
    // mousedown and mouseup -> the browser declines to fire click on the
    // swapped-out node -> the first click on a non-price pill feels dead.
    // Suppressing applySlider here is safe: removing a non-price pill uses
    // urlWithoutParam(), which preserves the existing price filter in the URL.
    window.__bertSuppressFormChange = true;
    window.__bertSuppressSliderApply = true;
  }, true);

  document.addEventListener('click', function (e) {
    var pill = e.target.closest('.active-facets-desktop > facet-remove > a, .active-facets-mobile > facet-remove > a');
    if (!pill) return;
    e.preventDefault();
    e.stopPropagation();
    var removeKey = pill.getAttribute('data-bert-remove-key');
    var removeValue = pill.getAttribute('data-bert-remove-value');
    // For the price pill: reset every slider's handles + text inputs to
    // their min/max defaults BEFORE we commit the new URL. This way any
    // later code that reads the slider (buildUrlFromForm, applySlider)
    // sees defaults and won't re-introduce a price filter.
    if (removeKey === '__price__') {
      document.querySelectorAll('[data-bert-price-slider]').forEach(function (slider) {
        var sliderMin = parseFloat(slider.getAttribute('data-slider-min')) || 0;
        var sliderMax = parseFloat(slider.getAttribute('data-slider-max')) || 100000;
        slider.querySelectorAll('[data-bert-slider-input="min"], [data-bert-slider-handle="min"]').forEach(function (el) { el.value = sliderMin; });
        slider.querySelectorAll('[data-bert-slider-input="max"], [data-bert-slider-handle="max"]').forEach(function (el) { el.value = sliderMax; });
        var rangeEl = slider.querySelector('[data-bert-slider-range]');
        if (rangeEl) { rangeEl.style.left = '0%'; rangeEl.style.right = '0%'; }
      });
    }
    var freshUrl;
    if (removeKey === '__price__') {
      freshUrl = urlWithoutPrice();
    } else if (removeKey) {
      freshUrl = urlWithoutParam(removeKey, removeValue);
    } else {
      // Fallback for older pill HTML that didn't store data attrs
      freshUrl = pill.getAttribute('href');
      if (!freshUrl) return;
    }
    commit(freshUrl);
    // Clear the suppress flags AFTER this task completes - blur/change
    // events have all flushed by then. Two ticks of separation: the inner
    // setTimeout ensures any queued blur->change->onFormChange chain runs
    // (and bails) before flags clear.
    setTimeout(function () {
      window.__bertSuppressSliderApply = false;
      window.__bertSuppressFormChange = false;
    }, 0);
  }, true);

  function syncFormStateFromUrl() {
    var forms = document.querySelectorAll(FILTER_FORM_SELECTOR);
    if (!forms.length) return;
    var params = new URLSearchParams(window.location.search);
    var allParams = {};
    params.forEach(function (v, k) {
      if (!allParams[k]) allParams[k] = [];
      allParams[k].push(v);
    });
    forms.forEach(function (form) {
      form.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
        var name = input.getAttribute('name');
        var val = input.getAttribute('value');
        input.checked = !!(allParams[name] && allParams[name].indexOf(val) !== -1);
      });
      form.querySelectorAll('input[type="hidden"][name^="filter.v.price"]').forEach(function (i) { i.remove(); });
      if (allParams['filter.v.price.gte']) {
        var h1 = document.createElement('input');
        h1.type = 'hidden'; h1.name = 'filter.v.price.gte'; h1.value = allParams['filter.v.price.gte'][0];
        form.appendChild(h1);
      }
      if (allParams['filter.v.price.lte']) {
        var h2 = document.createElement('input');
        h2.type = 'hidden'; h2.name = 'filter.v.price.lte'; h2.value = allParams['filter.v.price.lte'][0];
        form.appendChild(h2);
      }
    });
    // Price preset visual state
    var presets = document.querySelectorAll('.bert-price-preset');
    presets.forEach(function (p) {
      var href = p.getAttribute('href') || '';
      var hrefParams = new URLSearchParams(href.split('?')[1] || '');
      var pGte = hrefParams.get('filter.v.price.gte');
      var pLte = hrefParams.get('filter.v.price.lte');
      var matches = String(pGte || '') === String(allParams['filter.v.price.gte'] ? allParams['filter.v.price.gte'][0] : '')
                 && String(pLte || '') === String(allParams['filter.v.price.lte'] ? allParams['filter.v.price.lte'][0] : '');
      if (matches && (pGte || pLte)) p.classList.add('is-active');
      else p.classList.remove('is-active');
    });
  }

  // Patch Dawn's onSubmitForm to no-op now that we handle everything client-side.
  function patchFacetFiltersForm() {
    if (typeof FacetFiltersForm === 'undefined') return false;
    var proto = FacetFiltersForm.prototype;
    if (proto._bertPatched) return true;
    proto.onSubmitForm = function () { /* no-op - bert-client-filter handles everything */ };
    proto._bertPatched = true;
    return true;
  }

  // Patch Dawn's FacetRemove custom element. Without this, every pill
  // click triggers Dawn's closeFilter -> onActiveFilterClick -> renderPage
  // which does a FULL server fetch of the facets form HTML and REPLACES
  // the DOM. That tears down the price slider's event listeners. Rapid
  // pill clicks => slider DOM destroyed => slider stops responding to
  // typing/dragging. Patching to no-op stops Dawn's behavior; our
  // document-level click handler handles pill removal client-side.
  function patchFacetRemove() {
    if (typeof FacetRemove === 'undefined') return false;
    var proto = FacetRemove.prototype;
    if (proto._bertPatched) return true;
    proto.closeFilter = function (e) { if (e && e.preventDefault) e.preventDefault(); };
    proto._bertPatched = true;
    return true;
  }

  // Intercept Reset link clicks. Capture phase + stopPropagation so we beat
  // Dawn's menu-drawer / details click handlers. Each step wrapped in try/catch
  // so one failure doesn't block the others. Re-runs applyFilter after a
  // tick to defend against any other code that re-hides products meanwhile.
  function interceptResetLink() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.bert-filter-row__reset-link');
      if (!link) return;
      e.preventDefault();
      e.stopPropagation();

      try {
        var forms = document.querySelectorAll(FILTER_FORM_SELECTOR);
        forms.forEach(function (form) {
          form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach(function (i) {
            i.checked = false;
          });
          form.querySelectorAll('input[type="hidden"][name^="filter.v.price"]').forEach(function (i) {
            i.remove();
          });
        });
      } catch (err) { console.warn('[bert-filter] reset forms failed', err); }

      try {
        document.querySelectorAll('.bert-price-preset.is-active').forEach(function (p) {
          p.classList.remove('is-active');
        });
      } catch (err) { console.warn('[bert-filter] reset price presets failed', err); }

      // Reset every dual-handle price slider back to its full min/max range
      try {
        document.querySelectorAll('[data-bert-price-slider]').forEach(function (slider) {
          var sliderMin = parseFloat(slider.getAttribute('data-slider-min')) || 0;
          var sliderMax = parseFloat(slider.getAttribute('data-slider-max')) || 100000;
          slider.querySelectorAll('[data-bert-slider-handle="min"]').forEach(function (el) { el.value = sliderMin; });
          slider.querySelectorAll('[data-bert-slider-handle="max"]').forEach(function (el) { el.value = sliderMax; });
          slider.querySelectorAll('[data-bert-slider-input="min"]').forEach(function (el) { el.value = sliderMin; });
          slider.querySelectorAll('[data-bert-slider-input="max"]').forEach(function (el) { el.value = sliderMax; });
          var rangeEl = slider.querySelector('[data-bert-slider-range]');
          if (rangeEl) {
            rangeEl.style.left = '0%';
            rangeEl.style.right = '0%';
          }
        });
      } catch (err) { console.warn('[bert-filter] reset slider failed', err); }

      try {
        // Preserve sort_by when resetting filters (only filter.* params get cleared).
        var current = new URLSearchParams(window.location.search);
        var sortBy = current.get('sort_by');
        var newUrl = window.location.pathname + (sortBy ? '?sort_by=' + encodeURIComponent(sortBy) : '');
        window.history.pushState({ bertFilter: true }, '', newUrl);
      } catch (err) { console.warn('[bert-filter] pushState failed', err); }

      try { applyFilter(); } catch (err) { console.warn('[bert-filter] applyFilter failed', err); }
      try { renderActivePills(); } catch (err) { console.warn('[bert-filter] renderActivePills failed', err); }

      // Force-show every grid item directly as a belt-and-suspenders measure
      // in case applyFilter ran but some prior state left items hidden.
      try {
        document.querySelectorAll('.grid__item.bert-filter-hidden').forEach(function (i) {
          i.classList.remove('bert-filter-hidden');
        });
      } catch (err) {}

      try {
        var drawer = link.closest('menu-drawer.mobile-facets__wrapper, .mobile-facets__wrapper');
        if (drawer && typeof drawer.closeMenuDrawer === 'function') {
          var details = drawer.querySelector('details.mobile-facets__disclosure');
          var summary = details && details.querySelector('summary');
          drawer.closeMenuDrawer({ preventDefault: function(){}, stopPropagation: function(){} }, summary);
        } else if (drawer) {
          var d = drawer.querySelector('details.mobile-facets__disclosure');
          if (d && d.open) d.open = false;
        }
      } catch (err) { console.warn('[bert-filter] drawer close failed', err); }

      try {
        document.body.classList.remove('overflow-hidden-mobile');
        document.body.classList.remove('overflow-hidden-tablet');
      } catch (err) {}

      // Defensive: re-apply after a tick in case drawer-close animation or
      // any other async code re-hid items during this synchronous flow.
      setTimeout(function () {
        try { applyFilter(); } catch (err) {}
        try {
          document.querySelectorAll('.grid__item.bert-filter-hidden').forEach(function (i) {
            i.classList.remove('bert-filter-hidden');
          });
        } catch (err) {}
      }, 50);
    }, true); // capture phase
  }

  // Dual-handle price range slider - listens for input/change events on the
   // two overlapping range inputs, updates the filled range visual + value
   // display continuously while dragging, and applies the filter to URL/grid
   // on release (change event).
  function initPriceSliders() {
    document.querySelectorAll('[data-bert-price-slider]').forEach(function (slider) {
      var minHandle = slider.querySelector('[data-bert-slider-handle="min"]');
      var maxHandle = slider.querySelector('[data-bert-slider-handle="max"]');
      var minTextInput = slider.querySelector('[data-bert-slider-input="min"]');
      var maxTextInput = slider.querySelector('[data-bert-slider-input="max"]');
      var rangeEl = slider.querySelector('[data-bert-slider-range]');
      if (!minHandle || !maxHandle || !rangeEl) return;
      var sliderMin = parseFloat(slider.getAttribute('data-slider-min')) || 0;
      var sliderMax = parseFloat(slider.getAttribute('data-slider-max')) || 100000;

      // Sync internal state ↔ all four inputs (2 sliders + 2 text fields).
      // triggerSource: 'minHandle' | 'maxHandle' | 'minText' | 'maxText'
      function update(triggerSource) {
        var minVal = parseFloat(triggerSource && triggerSource.indexOf('Text') > -1 ? minTextInput.value : minHandle.value);
        var maxVal = parseFloat(triggerSource && triggerSource.indexOf('Text') > -1 ? maxTextInput.value : maxHandle.value);
        if (isNaN(minVal)) minVal = sliderMin;
        if (isNaN(maxVal)) maxVal = sliderMax;
        // Clamp to slider bounds
        if (minVal < sliderMin) minVal = sliderMin;
        if (maxVal > sliderMax) maxVal = sliderMax;
        // Enforce min <= max
        if (minVal > maxVal) {
          if (triggerSource === 'minHandle' || triggerSource === 'minText') maxVal = minVal;
          else minVal = maxVal;
        }
        // Push back to all inputs
        minHandle.value = minVal;
        maxHandle.value = maxVal;
        if (document.activeElement !== minTextInput) minTextInput.value = minVal;
        if (document.activeElement !== maxTextInput) maxTextInput.value = maxVal;
        // Update visual range
        var range = sliderMax - sliderMin;
        var leftPct = range > 0 ? ((minVal - sliderMin) / range) * 100 : 0;
        var rightPct = range > 0 ? ((maxVal - sliderMin) / range) * 100 : 100;
        rangeEl.style.left = leftPct + '%';
        rangeEl.style.right = (100 - rightPct) + '%';
      }
      function applySlider() {
        // Bail if a pill click is in progress (text input blur fires BEFORE
        // pill click - without this guard we'd re-add the filter the user
        // is removing). See the pill mousedown handler at the document
        // level for the flag-set side.
        if (window.__bertSuppressSliderApply) return;
        var minVal = parseFloat(minHandle.value);
        var maxVal = parseFloat(maxHandle.value);
        var current = new URLSearchParams(window.location.search);
        current.delete('filter.v.price.gte');
        current.delete('filter.v.price.lte');
        if (minVal > sliderMin) current.append('filter.v.price.gte', minVal);
        if (maxVal < sliderMax) current.append('filter.v.price.lte', maxVal);
        var qs = current.toString();
        document.querySelectorAll('.bert-price-preset.is-active').forEach(function (p) {
          p.classList.remove('is-active');
        });
        commit(window.location.pathname + (qs ? '?' + qs : ''));
      }
      // Debounced apply for keystroke streams (typing). 250ms after last
      // input fires applySlider once. Reset on every keystroke.
      var debounceTimer;
      function scheduleApply() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(applySlider, 250);
      }
      // Slider handles - 'input' fires during drag, 'change' fires on release.
      // Update visual state on every drag tick; commit URL only on release
      // (so the URL doesn't churn during drag).
      minHandle.addEventListener('input', function () { update('minHandle'); });
      maxHandle.addEventListener('input', function () { update('maxHandle'); });
      minHandle.addEventListener('change', function () { update('minHandle'); applySlider(); });
      maxHandle.addEventListener('change', function () { update('maxHandle'); applySlider(); });
      // Text inputs - debounced apply on typing, immediate apply on blur/Enter.
      minTextInput.addEventListener('input', function () { update('minText'); scheduleApply(); });
      maxTextInput.addEventListener('input', function () { update('maxText'); scheduleApply(); });
      minTextInput.addEventListener('blur', function () {
        clearTimeout(debounceTimer);
        update('minText');
        applySlider();
      });
      maxTextInput.addEventListener('blur', function () {
        clearTimeout(debounceTimer);
        update('maxText');
        applySlider();
      });
      minTextInput.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); minTextInput.blur(); } });
      maxTextInput.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); maxTextInput.blur(); } });
      // initial render
      update();
    });
  }

  // Intercept price preset link clicks. CRITICAL: build URL from CURRENT
   // active filters (URL state) + the preset's price params, NOT from the
   // preset's static href. The static href is rendered server-side at page
   // load and doesn't reflect filters added client-side after - using it
   // would silently drop those filters.
  function interceptPricePresets() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.bert-price-preset');
      if (!link) return;
      var href = link.getAttribute('href');
      if (!href) return;
      e.preventDefault();
      // Extract price params from the preset's href
      var hrefParams = new URLSearchParams(href.split('?')[1] || '');
      var presetGte = hrefParams.get('filter.v.price.gte');
      var presetLte = hrefParams.get('filter.v.price.lte');
      // Build new URL from CURRENT URL state, swapping out price params
      var current = new URLSearchParams(window.location.search);
      current.delete('filter.v.price.gte');
      current.delete('filter.v.price.lte');
      if (!link.classList.contains('bert-price-preset--clear')) {
        if (presetGte) current.append('filter.v.price.gte', presetGte);
        if (presetLte) current.append('filter.v.price.lte', presetLte);
      }
      var newQs = current.toString();
      var newUrl = window.location.pathname + (newQs ? '?' + newQs : '');
      // Update visual selection state
      document.querySelectorAll('.bert-price-preset').forEach(function (p) {
        p.classList.remove('is-active');
      });
      if (!link.classList.contains('bert-price-preset--clear')) {
        link.classList.add('is-active');
      }
      commit(newUrl);
    });
  }

  // Handle browser back/forward - use commit() but skip pushState (popstate
  // means the URL is ALREADY updated by the browser's history navigation).
  window.addEventListener('popstate', function () {
    syncFormStateFromUrl();
    applyFilter();
    renderActivePills();
  });

  function init() {
    if (!getGrid()) return;
    buildTaxonomyMap();
    var forms = document.querySelectorAll(FILTER_FORM_SELECTOR);
    forms.forEach(function (form) {
      form.addEventListener('change', onFormChange);
    });
    interceptResetLink();
    interceptPricePresets();
    initPriceSliders();

    // Patch Dawn custom elements ASAP. Try synchronously first (covers the
    // case where facets.js already loaded and registered them). If they're
    // not defined yet, use customElements.whenDefined() which fires the
    // microtask the moment Dawn calls customElements.define() - no
    // setInterval polling lag where a fast first-click could fire Dawn's
    // original onSubmitForm/closeFilter and trigger a full server re-render
    // (which is what was making the first filter click feel "lost").
    if (!patchFacetFiltersForm() && window.customElements && customElements.whenDefined) {
      customElements.whenDefined('facet-filters-form').then(patchFacetFiltersForm).catch(function(){});
    }
    if (!patchFacetRemove() && window.customElements && customElements.whenDefined) {
      customElements.whenDefined('facet-remove').then(patchFacetRemove).catch(function(){});
    }
    // Safety net: also poll for ~3s in case whenDefined misses (some Shopify
    // setups load facets.js after a slight delay and a stray instance might
    // be created before customElements.define).
    var tries = 0;
    var iv = setInterval(function () {
      var done = patchFacetFiltersForm() && patchFacetRemove();
      if (done || ++tries > 30) clearInterval(iv);
    }, 100);

    applyFilter();
    renderActivePills();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.bertClientFilter = { applyFilter: applyFilter, getActiveFilters: getActiveFilters, commit: commit };
})();
